import { Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import UpdateSubscriptionDialog from "../../../components/Dialogs/updateSubscriptionsDialog";
import {
  getAllSubscriptionsByRole,
  updateSubscription,
} from "../../../services/subscriptionApiFn";
import { handleOpenSnackBar } from "../../../utils";
import PlansCard from "./PlansCard";
import TabBar from "./TabBar";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

function AllPlans() {
  const openUpdateDialog = useRef(null);
  const { t } = useTranslation(["subscription", "common"]);
  const [allPlans, setAllPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [role, setRole] = useState("worker");
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  useEffect(() => {
    fetchAllPlans("worker");
  }, []);

  const fetchAllPlans = async (role) => {
    try {
      setIsLoading(true);
      const response = await getAllSubscriptionsByRole(role);
      if (response.data.status === "success") {
        setAllPlans(response.data.data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback((_, newValue) => {
    setRole(newValue);

    switch (newValue) {
      case "company":
        fetchAllPlans("company");
        break;
      case "supplier":
        fetchAllPlans("supplier");
        break;
      case "education":
        fetchAllPlans("education");
        break;
      default:
        fetchAllPlans("worker");
    }
  }, []);

  const handleOpenUpdateDialog = async (params) => {
    openUpdateDialog.current.openDialog({ params });
  };

  const updateSubscriptionPrice = async (params) => {
    try {
      setIsLoading(true);
      const response = await updateSubscription(params.params._id, {
        price: params.newPrice,
        yearlyPrice: params.yearlyPrice,
      });
      if (response.data.status === "success") {
        fetchAllPlans(role);
        openUpdateDialog.current.closeDialog();
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("allSubscriptions")}
        subtitle={t("viewAndUpdateSubscription")}
      >
        <TabBar
          type={"subscription"}
          value={role}
          handleChange={handleChange}
        />

        {allPlans?.length > 0 ? (
          <Grid container spacing={2} mt={1}>
            {allPlans?.map((plan, index) => (
              <PlansCard
                key={index}
                val={plan}
                handleUpdatePrice={handleOpenUpdateDialog}
                language={currentLanguage}
              />
            ))}
          </Grid>
        ) : allPlans?.length === 0 && isLoading === false ? (
          <Typography variant="h5" my={4} fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}!
          </Typography>
        ) : null}
      </PaperContainer>
      <UpdateSubscriptionDialog
        ref={openUpdateDialog}
        onConfirm={updateSubscriptionPrice}
      />
    </>
  );
}

AllPlans.propTypes = {
  plansData: PropTypes.array,
};

export default AllPlans;
